/* Domovoi · the installable web app (D1.1): registers the service worker,
   keeps its cache of signed-in data to the person who is signed in, and
   answers "is this running as an installed app?".

   Runs once from site.js on every page. Not inside the native shell: the app
   ships its own bundle there and must not have a worker caching underneath it. */
import { isNative } from './platform.js';
import { stored, fromToken } from './session.js';
import { storage } from './storage.js';

var DATA_CACHE = 'dv-data';
var UID_KEY = 'domovoi.sw.uid';
var OFF_KEY = 'domovoi.sw.off';

/* Installed: launched from the home screen (Android, desktop) or added to the
   iOS home screen (navigator.standalone). */
export function isInstalled() {
  try {
    return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
      || window.navigator.standalone === true;
  } catch (e) { return false; }
}

/* Chrome's install prompt (Android, desktop), held so the home hub's card can
   offer one-tap install (D1.2). Null where the browser has none to give. */
var deferredPrompt = null;
export function installPrompt() { return deferredPrompt; }

/* Forget cached signed-in reads (sign-out, or someone else signs in). */
export function clearData() {
  try { if (window.caches) return caches.delete(DATA_CACHE); } catch (e) {}
  return Promise.resolve(false);
}

/* ?sw=off: unregister and clear everything in this browser, and keep it off
   here until ?sw=on. The local half of the kill switch, and the quickest way to
   rule the worker out when debugging. (The remote half is a deploy with
   DOMOVOI_SW_KILL=1 — see AGENTS.md.) */
function killLocally() {
  var jobs = [];
  if (navigator.serviceWorker) {
    jobs.push(navigator.serviceWorker.getRegistrations().then(function (regs) {
      return Promise.all(regs.map(function (r) { return r.unregister(); }));
    }));
  }
  if (window.caches) {
    jobs.push(caches.keys().then(function (keys) {
      return Promise.all(keys.filter(function (k) { return k.indexOf('dv-') === 0; }).map(function (k) { return caches.delete(k); }));
    }));
  }
  return Promise.all(jobs);
}

/* The cached reads belong to whoever was signed in when they were fetched.
   A different person, or nobody, means they go. */
function guardData() {
  var s = stored();
  var uid = s ? fromToken(s).id : '';
  var last = storage.get(UID_KEY) || '';
  if (uid !== last) {
    clearData();
    if (uid) storage.set(UID_KEY, uid); else storage.remove(UID_KEY);
  }
}

export function start() {
  if (isNative() || !('serviceWorker' in navigator)) return;
  var params = new URLSearchParams(location.search);
  var flag = params.get('sw');
  if (flag === 'on') storage.remove(OFF_KEY);
  if (flag === 'off') {
    storage.set(OFF_KEY, '1');
    // This page is still served by the worker until it reloads, and would
    // refill the caches as it loads; so clear, then reload without the flag.
    killLocally().then(function () {
      params.delete('sw');
      var q = params.toString();
      location.replace(location.pathname + (q ? '?' + q : '') + location.hash);
    });
    return;
  }
  if (storage.get(OFF_KEY)) { killLocally(); return; }
  guardData();
  window.addEventListener('beforeinstallprompt', function (e) {
    e.preventDefault();               // no mini-infobar; the hub card offers it instead
    deferredPrompt = e;
    window.dispatchEvent(new Event('dv-installable'));
  });
  window.addEventListener('appinstalled', function () { deferredPrompt = null; });
  window.addEventListener('load', function () {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(function () {});
  });
}
